import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import {
  useFlags,
  useOpenDeck,
  usePass,
  usePassDecks,
  usePassEquip,
  useProfile,
  useQualification,
} from '../api/hooks';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { NickFx } from '../components/cosmetics/NickFx';
import { ProfileBg } from '../components/cosmetics/ProfileBg';
import { RowFxLayer, rowFxClass } from '../components/cosmetics/RowFx';
import { DeckBox } from '../components/pass/DeckBox';
import { SlotGlyph } from '../components/pass/RewardPreview';
import {
  RARITY_COLOR,
  RARITY_LABEL,
  RARITY_LABEL_M,
  RARITY_TEXT_DARK,
  rarityTextStyle,
  rarityTint,
} from '../components/cosmetics/cosmeticsMap';
import eternalUrl from '../assets/pass/celebrations/eternal_freeze.lottie';
import { backButton, haptics } from '../telegram';
import { resolvedTheme } from '../theme';
import { track } from '../analytics';
import type { PassOpenResult } from '../api/types';

type Phase = 'idle' | 'open' | 'flip' | 'result';

/** Подписи слота на 6.5b — полные, как в макете («Строка в списках»). */
const FS_SLOT_LABEL: Record<string, string> = {
  frame: 'Рамка',
  nick: 'Никнейм',
  row: 'Строка в списках',
  bg: 'Фон профиля',
};

const FREEZE_NAMES: Record<string, string> = {
  game1: 'Заморозка на 1 игру',
  game3: 'Заморозка на 3 игры',
  week: 'Заморозка на неделю',
  eternal: 'Вечная заморозка',
};


/**
 * 6.5 — открытие колоды: тёмная сцена, straddle-deck.lottie со стадиями
 * idle → (тап) open → flip → шторка результата. Сервер роллит результат в
 * момент тапа; анимация играет, пока ответ уже на руках. ?all=1 — режим
 * «открыть все»: после «Дальше» едем к следующей неоткрытой.
 */
/** eternal_freeze.lottie по сегментам: intro 0–120 один раз → loop 120–240;
 *  haptic success на «сборке снежинки» (~кадр 44); reduced-motion — кадр 120. */
function EternalLottie({ full = false }: { full?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduce =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dl = new DotLottie({ canvas, src: eternalUrl, autoplay: false, loop: false });
    dl.addEventListener('load', () => {
      if (reduce) {
        dl.setFrame(120);
        dl.pause();
        return;
      }
      dl.setSegment(0, 120);
      dl.play();
    });
    dl.addEventListener('complete', () => {
      dl.setSegment(120, 240);
      dl.setLoop(true);
      dl.play();
    });
    return () => {
      dl.destroy();
    };
  }, []);
  const size = full ? Math.min(window.innerWidth, 480) : 320;
  return <canvas ref={canvasRef} width={size * 2} height={size * 2} style={{ width: size, height: size }} aria-hidden />;
}

/** 5.3 — «Одна на весь клуб»: полноэкранное празднование вечной заморозки. */
function EternalScreen({ onDone }: { onDone: () => void }) {
  const flags = useFlags();
  const pass = usePass(Boolean(flags.data?.pass));
  const navigate = useNavigate();
  const daysLeft = pass.data?.season?.daysLeft ?? null;
  return (
    <div className="bp-eternal-screen">
      <div className="bp-eternal-lottie" aria-hidden>
        <EternalLottie full />
      </div>
      <div className="bp-eternal-body">
        <div className="bp-overline">ОДНА НА ВЕСЬ КЛУБ</div>
        <h2>Вечная заморозка</h2>
        <p>Ты получил единственную вечную заморозку сезона</p>
        <div className="bp-eternal-stats">
          {daysLeft !== null && (
            <span>
              <b className="mono">{daysLeft}</b>дн. до конца отбора
            </span>
          )}
          <span>
            <b className="mono">∞</b>пропусков покрыто
          </span>
        </div>
        <button
          className="btn"
          onClick={() => {
            haptics.tap();
            onDone();
          }}
        >
          Отлично
        </button>
        <button
          className="bp-eternal-link"
          onClick={() => {
            haptics.tap();
            navigate('/pass/freezes');
          }}
        >
          К заморозкам
        </button>
      </div>
    </div>
  );
}

/** Гексагон вечной/заморозки в превью 6.5b (из макета колод). */
function FreezeHex() {
  return (
    <svg width="84" height="84" viewBox="0 0 48 48" fill="none" aria-hidden>
      <defs>
        <linearGradient id="bp-fshex" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#9FE3FA" />
          <stop offset=".55" stopColor="#E9F7FF" />
          <stop offset="1" stopColor="#C8A04E" />
        </linearGradient>
      </defs>
      <path d="M24 2.5 42.6 13.25v21.5L24 45.5 5.4 34.75v-21.5z" fill="#0A1A33" stroke="url(#bp-fshex)" strokeWidth="2" />
      <path d="M24 9v6M24 33v6M11 16.5l5.2 3M31.8 28.5l5.2 3M11 31.5l5.2-3M31.8 19.5l5.2-3" stroke="#9FE3FA" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M24 24c-2-2.6-6.4-2.6-6.4 0s4.4 2.6 6.4 0 6.4-2.6 6.4 0-4.4 2.6-6.4 0z" stroke="#D9B464" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Превью внутри «карты-экрана» (handoff 5 п.3): 200×112, тёмное в обеих
 *  темах, живой эффект циклом; игрок — со всей НАДЕТОЙ косметикой, слот
 *  нового предмета подменяется им самим. */
function CardPreview({ result, fxOn }: { result: PassOpenResult; fxOn: boolean }) {
  const me = useProfile(null);
  const qual = useQualification();
  const name = me.data?.displayName ?? 'Игрок';
  const item = result.item;
  if (result.resultKind === 'freeze') {
    return (
      <span className="pv pv--frost">
        <FreezeHex />
      </span>
    );
  }
  if (!item) return null;
  const c = { code: item.code, kind: item.kind, asset: item.asset, rarity: item.rarity };
  const eq = me.data?.cosmetics;
  if (result.resultKind === 'dup') {
    return (
      <span className="pv">
        <span className="pv-dup">
          <span className="ghost">
            {item.slot === 'frame' ? (
              <CosmeticAvatar name={name} src={me.data?.photoUrl} size={56} frame={c} />
            ) : item.slot === 'nick' ? (
              <NickFx cosmetic={c}>{name}</NickFx>
            ) : (
              <SlotGlyph slot={item.slot} size={26} />
            )}
          </span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8088A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
          <span className="plus">
            <b className="mono">+50</b>
            <i>XP Straddle Pass</i>
          </span>
        </span>
      </span>
    );
  }
  const myQual =
    qual.data?.status === 'active' || qual.data?.status === 'finished' ? qual.data.me : null;
  return (
    <span className={`pv${item.slot === 'bg' ? ' pbg-host' : ''}${item.slot === 'row' ? ` rfx-host${rowFxClass(c)}` : ''}`}>
      {item.slot === 'row' && fxOn && <RowFxLayer cosmetic={c} height={112} mode="loop" />}
      {item.slot === 'bg' && <ProfileBg cosmetic={c} />}
      {item.slot === 'frame' ? (
        <CosmeticAvatar name={name} src={me.data?.photoUrl} size={72} frame={c} />
      ) : item.slot === 'nick' ? (
        <span className="pv-nick">
          <NickFx cosmetic={c}>{name}</NickFx>
        </span>
      ) : (
        <span className="pv-row">
          <CosmeticAvatar
            name={name}
            src={me.data?.photoUrl}
            size={44}
            frame={item.slot === 'bg' ? eq?.frame : (eq?.frame ?? undefined)}
          />
          <span className="who">
            <b>{name}</b>
            <i className="mono">
              {myQual && myQual.rank !== null ? `${myQual.rating} · #${myQual.rank}` : 'отбор впереди'}
            </i>
          </span>
        </span>
      )}
    </span>
  );
}

const CARD_SLOT_GIVES: Record<string, string> = {
  row: 'Эффект твоей строки в рейтинге и списках записавшихся',
  frame: 'Рамка вокруг твоей аватарки в списках и профиле',
  nick: 'Цвет твоего ника везде, где тебя видно',
  bg: 'Фон твоего профиля — увидит каждый, кто его откроет',
};

/** «Карта-экран» (handoff 5, 4b/5b): панель 248×350 — ♠, ромбы+редкость,
 *  слот, превью, название, «что даёт», футер с редкостью колоды и датой. */
function ResultCard({
  result,
  deckRarity,
  fxOn,
}: {
  result: PassOpenResult;
  deckRarity: number;
  fxOn: boolean;
}) {
  const isFreeze = result.resultKind === 'freeze';
  const r = result.item?.rarity ?? (result.freezeKind === 'eternal' ? 3 : deckRarity);
  const name = isFreeze
    ? (FREEZE_NAMES[result.freezeKind ?? ''] ?? 'Заморозка')
    : (result.item?.name ?? '');
  const gives = isFreeze
    ? result.freezeKind === 'eternal'
      ? 'Баллы отбора не убывают до конца сезона'
      : 'Баллы отбора не сгорят за пропуск игры'
    : result.resultKind === 'dup'
      ? 'Повтор превращается в опыт Straddle Pass'
      : (CARD_SLOT_GIVES[result.item?.slot ?? ''] ?? '');
  return (
    <div
      className="bp-rescard rtx"
      style={{
        ...rarityTextStyle(r),
        ['--rc-e' as string]: RARITY_COLOR[r],
        ['--rc-bd' as string]: rarityTint(r, 0.45),
        ['--rc-glow' as string]: r >= 2 ? `0 0 ${r === 3 ? 22 : 18}px ${rarityTint(r, 0.55)}` : 'none',
      }}
    >
      <span className="suit" aria-hidden>♠</span>
      <div className="rar mono">
        <span className="pips" aria-hidden>
          {Array.from({ length: r + 1 }, (_, i) => (
            <i key={i} style={{ background: RARITY_COLOR[r] }} />
          ))}
        </span>
        {RARITY_LABEL_M[r]}
      </div>
      <div className="slot mono">
        {isFreeze ? 'Заморозка' : (FS_SLOT_LABEL[result.item!.slot] ?? '')}
        {result.resultKind === 'dup' ? ' · повтор' : ''}
      </div>
      <CardPreview result={result} fxOn={fxOn} />
      <div className={`nm${result.resultKind === 'dup' ? ' dup' : ''}`}>{name}</div>
      <div className="gives">{gives}</div>
    </div>
  );
}

export function DeckOpenScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const { id } = useParams();
  const deckId = Number(id);
  const [params] = useSearchParams();
  const openAll = params.get('all') === '1';
  const navigate = useNavigate();
  const open = useOpenDeck();
  const equip = usePassEquip();
  const decks = usePassDecks(enabled);
  const [phase, setPhase] = useState<Phase>('idle');
  const resultRef = useRef<PassOpenResult | null>(null);
  const [result, setResult] = useState<PassOpenResult | null>(null);

  useEffect(() => {
    // -1: экран открыт только из инвентаря; push «/pass/decks» дублировал
    // инвентарь в истории, и системный «назад» ходил по кругу.
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  const deck = decks.data?.decks.find((d) => d.id === deckId);
  // После открытия колода уходит из списка — мету держим с момента входа,
  // чтобы шапка 6.5b не теряла «Игровая · Пт 26.09».
  const deckInfoRef = useRef<{ source: string; obtainedAt: string } | null>(null);
  if (deck) deckInfoRef.current = { source: deck.source, obtainedAt: deck.obtainedAt };
  const deckInfo = deck ?? deckInfoRef.current;
  const rarity = result?.rarity ?? deck?.rarity ?? 0;

  // Сценарий 6.5 по таймингам handoff 4: тап → open, через 1,5 с flip
  // (0,62 с, карта ×1,7, лицо через rotateY), через 1,6 с — слой 6.5b.
  // Повторный тап пропускает ожидание. Хуки — ДО любых ранних return
  // (rules of hooks: иначе смена флага роняла рендер).
  const timers = useRef<number[]>([]);
  // «Смотреть» у вечной заморозки: слой прячется, остаётся сцена с lottie.
  const [viewingEternal, setViewingEternal] = useState(false);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  // 6.5b (handoff 4): живой эффект превью стартует через 350 мс после
  // появления слоя, сцена под непрозрачным слоем размонтируется через 450 мс.
  const [fxOn, setFxOn] = useState(false);
  const [sceneGone, setSceneGone] = useState(false);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (phase !== 'result') {
      setFxOn(false);
      setSceneGone(false);
      setClosing(false);
      return;
    }
    const t1 = window.setTimeout(() => setFxOn(true), 350);
    const t2 = window.setTimeout(() => setSceneGone(true), 450);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [phase]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;

  // Правка владельца: без переворота карты — после веера сразу результат.
  const toFlip = () => {
    setPhase((p) => {
      if (p !== 'open') return p;
      if (resultRef.current?.freezeKind === 'eternal') haptics.success();
      else haptics.light();
      return 'result';
    });
  };

  const tap = () => {
    if (phase !== 'idle') {
      if (phase === 'open') toFlip();
      return;
    }
    if (rarity === 3) haptics.heavy();
    else haptics.medium();
    track('pass_deck_open', { deckId, rarity });
    setPhase('open');
    timers.current.push(window.setTimeout(toFlip, 1700));
    void open
      .mutateAsync(deckId)
      .then((r) => {
        resultRef.current = r;
        setResult(r);
      })
      .catch(() => {
        haptics.error();
        navigate(-1);
      });
  };

  /** Закрытие слоя 6.5b: opacity → 0 за 220 мс, затем переход. */
  const close = () => {
    setClosing(true);
    timers.current.push(window.setTimeout(next, 220));
  };

  const next = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    const rest = (decks.data?.decks ?? []).filter((d) => d.id !== deckId);
    if (openAll && rest.length) {
      setPhase('idle');
      setViewingEternal(false);
      setResult(null);
      resultRef.current = null;
      navigate(`/pass/decks/${rest[0].id}/open?all=1`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  // 6.5b: подпись шапки «Игровая · Пт 26.09» (с днём недели — макет).
  const deckMeta = deckInfo
    ? `${deckInfo.source === 'bought' ? 'Купленная' : 'Игровая'} · ${new Date(deckInfo.obtainedAt)
        .toLocaleDateString('ru-RU', { weekday: 'short', day: '2-digit', month: '2-digit' })
        .replace(',', '')
        .replace(/^./, (ch) => ch.toUpperCase())}`
    : null;

  const rest = (decks.data?.decks ?? []).filter((d) => d.id !== deckId);
  const continueAll = openAll && rest.length > 0;
  const secondaryLabel = continueAll
    ? 'Дальше'
    : result?.resultKind === 'dup'
      ? 'К Straddle Pass'
      : result?.resultKind === 'freeze'
        ? 'К заморозкам'
        : 'В гардероб';
  const secondaryAct = () => {
    haptics.tap();
    if (continueAll) {
      close();
      return;
    }
    if (result?.resultKind === 'freeze') {
      navigate('/pass/freezes');
      return;
    }
    if (result?.resultKind === 'dup') {
      navigate('/pass');
      return;
    }
    navigate('/pass/wardrobe');
  };

  const note = !result
    ? ''
    : result.resultKind === 'dup'
      ? 'Уже есть в гардеробе — повтор превращается в опыт Straddle Pass'
      : result.resultKind === 'freeze'
        ? result.freezeKind === 'eternal'
          ? 'Единственная на весь клуб. Баллы отбора не убывают за пропуски до конца сезона.'
          : 'Защитит баллы отбора от списаний за пропуск'
        : result.item?.slot === 'row'
          ? `Эффект играет ${result.item.rarity === 3 ? 'циклом' : 'раз в ~10 секунд'} везде, где есть твоя строка`
          : result.item?.slot === 'frame'
            ? 'Рамку увидят в рейтинге, списках записавшихся и профиле'
            : result.item?.slot === 'nick'
              ? 'Ник подсвечен везде, где тебя видно'
              : 'Фон виден всем, кто откроет твой профиль';

  const head = (onClose: () => void) => (
    <div className="bp-open-head">
      <span className="bp-open-head-main">
        <span className="bp-open-title mono" style={{ color: RARITY_TEXT_DARK[rarity] }}>
          {RARITY_LABEL[rarity]} колода
        </span>
        {deckMeta && <span className="bp-open-src">{deckMeta}</span>}
      </span>
      <button
        className="bp-open-x"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Закрыть"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9DA6BE" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="bp-open-scene" data-theme="dark" onClick={tap}>
      {!sceneGone && (
        <>
          {head(() => navigate(-1))}

          <span className="bp-open-deck" role="button" aria-label="Открыть колоду">
            {/* переворота нет (правка владельца): после веера — сразу экран */}
            <DeckBox rarity={rarity} stage={phase === 'idle' ? 'idle' : 'open'} size={150} />
          </span>

          {phase === 'result' &&
            (result ? result.item?.rarity === 3 || result.freezeKind === 'eternal' : rarity === 3) && (
              <div className="bp-open-goldflash" aria-hidden />
            )}

          <div className="bp-open-hint">
            <span key={phase}>
              {phase === 'idle' && 'Нажми на колоду'}
              {phase === 'open' && 'Открываем…'}
            </span>
          </div>
        </>
      )}

      {viewingEternal && <EternalScreen onDone={next} />}
      {phase === 'result' && result && !viewingEternal && (
        <div
          className={`bp-card-fs${closing ? ' bp-card-fs--out' : ''}`}
          // сцена под слоем пришпилена к тёмной теме — слой перепришпиливаем
          // к теме приложения (карта белая только в светлой, handoff 5 п.3.3)
          data-theme={resolvedTheme()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bp-open-fs-safe" />
          {head(close)}
          <div className="bp-card-fs-body">
            <div className="cap mono seq">Тебе выпало</div>
            <ResultCard result={result} deckRarity={rarity} fxOn={fxOn} />
            <div className="note seq">{note}</div>
          </div>
          <div className="bp-open-fs-actions">
            {result.freezeKind === 'eternal' && (
              <button
                className="btn"
                onClick={() => {
                  haptics.tap();
                  setViewingEternal(true);
                }}
              >
                Смотреть
              </button>
            )}
            {result.resultKind === 'item' && result.item && (
              <button
                className="btn"
                disabled={equip.isPending}
                onClick={() => {
                  haptics.tap();
                  void equip
                    .mutateAsync({ slot: result.item!.slot, itemId: result.item!.id })
                    .then(() => {
                      haptics.success();
                      if (continueAll) next();
                      else {
                        try {
                          sessionStorage.setItem('bp_equip_toast', `Надето · ${result.item!.name}`);
                        } catch {
                          /* private mode — просто без тоста */
                        }
                        navigate(-1);
                      }
                    })
                    .catch(() => haptics.error());
                }}
              >
                Надеть
              </button>
            )}
            <button className="btn-secondary bp-btn-secondary" onClick={secondaryAct}>
              {secondaryLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
